import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DraftCommentComponent.module.css';

const formatDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleString('ko-KR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
};

const postJson = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
});

/**
 * props:
 *   - commentInfo: { commentId, draftDocumentId, commenterEmail, commenterName, commentText, createdAt, updatedAt }
 *   - reDrawView: 수정/삭제 후 부모가 다시 호출할 콜백
 */
function DraftCommentComponent({ commentInfo, reDrawView }) {
    const { t } = useTranslation();

    const [userInfo, setUserInfo] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(commentInfo.commentText);
    const [saving, setSaving] = useState(false);

    // 현재 로그인 유저 정보 (본인 댓글 여부 판단용)
    useEffect(() => {
        fetch('/api/getLoginedUserInfo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        })
            .then(res => res.json())
            .then(data => setUserInfo(data.data[0] || {}))
            .catch(err => console.error('로그인 유저 정보 불러오기 실패:', err));
    }, []);

    const isOwnComment = userInfo?.email === commentInfo.commenterEmail;

    // update/delete의 공통 fetch 패턴
    const performCommentAction = async (url, body, errorMsgKey) => {
        const res = await postJson(url, body);
        if (!res.ok) {
            const errJson = await res.json();
            throw new Error(errJson.message || errorMsgKey);
        }
        reDrawView?.();
    };

    const handleEditClick = () => {
        setEditText(commentInfo.commentText);
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setIsEditing(false);
        setEditText(commentInfo.commentText);
    };

    const handleSaveClick = async () => {
        const trimmed = editText.trim();
        if (trimmed === '') return;

        setSaving(true);
        try {
            await performCommentAction(
                '/api/updateComment',
                {
                    commentId: commentInfo.commentId,
                    draftDocumentId: commentInfo.draftDocumentId,
                    newText: trimmed,
                },
                '댓글 수정에 실패했습니다.'
            );
            setIsEditing(false);
        } catch (error) {
            console.error('댓글 수정 중 오류:', error);
            alert(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = async () => {
        if (!window.confirm(t('이 댓글을 삭제하시겠습니까?'))) return;
        try {
            await performCommentAction(
                '/api/deleteComment',
                {
                    commentId: commentInfo.commentId,
                    draftDocumentId: commentInfo.draftDocumentId,
                },
                '댓글 삭제에 실패했습니다.'
            );
        } catch (error) {
            console.error('댓글 삭제 중 오류:', error);
            alert(error.message);
        }
    };

    return (
        <div className={styles.commentContainer}>
            <div className={styles.commentHeader}>
                <span className={styles.commentAuthor}>{commentInfo.commenterName}</span>
                <span className={styles.commentDate}>{formatDate(commentInfo.createdAt)}</span>
            </div>

            {isEditing ? (
                <textarea
                    className={styles.commentEditArea}
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                />
            ) : (
                <div className={styles.commentBody}>{commentInfo.commentText}</div>
            )}

            <div className={styles.commentActions}>
                {isOwnComment && !isEditing && (
                    <>
                        <button type="button" className={styles.commentEditButton}   onClick={handleEditClick}>수정</button>
                        <button type="button" className={styles.commentDeleteButton} onClick={handleDeleteClick}>삭제</button>
                    </>
                )}
                {isEditing && (
                    <>
                        <button type="button" className={styles.commentSaveButton}   onClick={handleSaveClick}   disabled={saving}>
                            {saving ? '저장 중...' : '저장'}
                        </button>
                        <button type="button" className={styles.commentCancelButton} onClick={handleCancelClick} disabled={saving}>
                            취소
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export default DraftCommentComponent;